import { ColorAxisOptions } from "highcharts";

import { TTemporalDataResponseSerie } from "@/components/ui/chart/types/temporal-chart-series.type";
import { CalendarHeatmapXAxisAdapter } from "@/utils/chart/adapters/axis/calendar-heatmap-x-axis-adapter";
import { CalendarHeatmapSeriesAdapter } from "@/utils/chart/adapters/series/calendar-heatmap-series-adapter";
import { ICalendarHeatmapAdapter } from "@/utils/chart/adapters/types/calendar-heatmap-adapter.type";
import { ICalendarHeatmapXAxisAdapter } from "@/utils/chart/adapters/types/common-adapter.type";
import { EDay } from "@/utils/date";

export interface ICalendarHeatmapSpecs<KIND extends string> {
  weekdaysFormatter: (day: EDay) => string;
  accessibilityDescription?: string;
  prefixSymbol?: string;
  unitSymbol?: string;
  serieAdapter?: ICalendarHeatmapAdapter<KIND>;
  xAxisAdapter?: ICalendarHeatmapXAxisAdapter<KIND>;
  colorStops?: ColorAxisOptions["stops"];
}

const defaultColorAxisStops: ColorAxisOptions["stops"] = [
  [0.1, "#85E5AA"],
  [0.5, "#5FD2FF"],
  [1, "#8979FF"],
];

const weekdays: EDay[] = Object.values(EDay);

export function buildCalendarHeatmapOptions<KIND extends string>(
  specs: ICalendarHeatmapSpecs<KIND>,
  serie: TTemporalDataResponseSerie<KIND>,
): Highcharts.Options {
  const unit = serie.unit;

  const serieAdapter = specs.serieAdapter ?? CalendarHeatmapSeriesAdapter();
  const serieOpts = serieAdapter.adapt(serie);

  const xAxisAdapter = specs.xAxisAdapter ?? CalendarHeatmapXAxisAdapter();
  const xAxisOpts = xAxisAdapter.adapt(serie);

  return {
    chart: {
      type: "heatmap",
      height: 300,
    },
    title: {
      text: "",
    },
    xAxis: xAxisOpts,
    yAxis: {
      categories: weekdays.map(specs.weekdaysFormatter),
      title: {
        text: null,
      },
      reversed: true,
      gridLineWidth: 0,
      height: "90%",
    },
    series: serieOpts,
    colorAxis: {
      stops: specs.colorStops ?? defaultColorAxisStops,
      reversed: false,
      labels: {
        formatter: (v) => `${v.value} ${specs.prefixSymbol ?? ""}${specs.unitSymbol ?? unit}`,
      },
    },
    plotOptions: {
      heatmap: {
        borderRadius: 6,
        pointPadding: 2,
      },
    },
    legend: {
      align: "right",
      layout: "vertical",
      verticalAlign: "top",
      symbolHeight: 190,
      y: 54,
      symbolRadius: 6,
      navigation: {
        enabled: false,
      },
    },
    credits: {
      enabled: false,
    },
  };
}
